<template>
  <div>
    <div class="send-email-modal__attachment-actions">
      <button
        v-if="showInvoiceAttachmentsAction"
        type="button"
        class="send-email-modal__link-btn"
        @click="emitToggleInvoiceAttachments"
      >
        <i class="fa fa-paperclip" aria-hidden="true"></i>
        <span>{{ resolvedText.attachInvoicesButtonLabel }}</span>
      </button>

      <button type="button" class="send-email-modal__link-btn" @click="emitToggleDocumentAttachments">
        <i class="fa fa-paperclip" aria-hidden="true"></i>
        <span>{{ resolvedText.attachDocumentsButtonLabel }}</span>
      </button>
    </div>

    <div v-if="showInvoiceAttachmentsAction && showInvoiceAttachments" class="send-email-modal__collapsible">
      <strong class="send-email-modal__group-title">{{ resolvedText.invoiceAttachmentsTitle }}</strong>

      <multiselect
        v-model="selectedInvoiceAttachmentsProxy"
        :options="normalizedInvoiceAttachmentOptions"
        track-by="id"
        label="label"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :preserve-search="true"
        :show-labels="false"
        :searchable="true"
        class="app-modal-select send-email-modal__invoice-select invoice-create-receiver-select"
        :placeholder="resolvedText.attachInvoicesButtonLabel"
      >
        <template #option="{ option }">
          <div class="send-email-modal__invoice-option">
            <small><strong>Folio:</strong> {{ option?.folio || '-' }}</small>
            <small><strong>Fecha:</strong> {{ option?.date || '-' }}</small>
            <small><strong>Monto:</strong> {{ option?.totalLabel || '-' }}</small>
            <small><strong>UUID:</strong> {{ option?.uuid || '-' }}</small>
          </div>
        </template>
        <template #noResult>
          <span>{{ resolvedText.emptyAttachmentsText }}</span>
        </template>
        <template #noOptions>
          <span>{{ resolvedText.emptyAttachmentsText }}</span>
        </template>
      </multiselect>

      <div class="send-email-modal__section-actions">
        <button
          type="button"
          class="send-email-modal__close-section-btn"
          @click="emitCloseInvoiceAttachments"
        >
          {{ resolvedText.closeSectionLabel }}
        </button>
      </div>
    </div>

    <div v-if="showDocumentAttachments" class="send-email-modal__collapsible send-email-modal__collapsible--documents">
      <strong class="send-email-modal__group-title">{{ resolvedText.documentAttachmentsTitle }}</strong>

      <div class="send-email-modal__document-mode-row">
        <label class="app-modal-toggle send-email-modal__document-mode-option">
          <input
            type="checkbox"
            :checked="isExistingDocumentMode"
            @change="emitSetDocumentAttachmentMode('existing')"
          />
          <span>{{ resolvedText.documentModeExistingLabel }}</span>
        </label>

        <label class="app-modal-toggle send-email-modal__document-mode-option">
          <input
            type="checkbox"
            :checked="!isExistingDocumentMode"
            @change="emitSetDocumentAttachmentMode('new')"
          />
          <span>{{ resolvedText.documentModeNewLabel }}</span>
        </label>
      </div>

      <multiselect
        v-if="isExistingDocumentMode"
        v-model="selectedDocumentAttachmentsProxy"
        :options="normalizedDocumentAttachmentOptions"
        track-by="id"
        label="label"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :preserve-search="true"
        :show-labels="false"
        :searchable="true"
        class="app-modal-select send-email-modal__invoice-select send-email-modal__document-select invoice-create-receiver-select"
        :placeholder="resolvedText.attachDocumentsButtonLabel"
      >
        <template #option="{ option }">
          <div class="send-email-modal__invoice-option">
            <small><strong>Documento:</strong> {{ option?.label || '-' }}</small>
          </div>
        </template>
        <template #noResult>
          <span>{{ resolvedText.emptyAttachmentsText }}</span>
        </template>
        <template #noOptions>
          <span>{{ resolvedText.emptyAttachmentsText }}</span>
        </template>
      </multiselect>

      <div v-if="!isExistingDocumentMode" class="send-email-modal__new-document-list">
        <div
          v-for="(upload, index) in newDocumentUploads"
          :key="`new-doc-${upload.id}`"
          class="send-email-modal__new-document-row"
        >
          <input
            :ref="`newDocInput-${upload.id}`"
            type="file"
            class="send-email-modal__new-document-input"
            @change="emitNewDocumentChange(upload.id, $event)"
          />

          <button
            type="button"
            class="app-header-btn app-header-btn--secondary send-email-modal__new-document-trigger"
            @click="openNativeDocumentPicker(upload.id)"
          >
            {{ resolvedText.selectFileLabel }}
          </button>

          <span class="send-email-modal__new-document-name">
            {{ upload.fileName || resolvedText.noFileSelectedLabel }}
          </span>

          <button
            v-if="index === newDocumentUploads.length - 1"
            type="button"
            class="app-header-btn app-header-btn--secondary send-email-modal__new-document-add"
            :title="resolvedText.addAnotherFileLabel"
            @click="emitAddNewDocumentUploadRow"
          >
            <i class="fa fa-plus" aria-hidden="true"></i>
          </button>

          <button
            v-else
            type="button"
            class="app-header-btn app-header-btn--secondary send-email-modal__new-document-remove"
            :title="resolvedText.removeAttachmentLabel"
            @click="emitRemoveNewDocumentUploadRow(upload.id)"
          >
            <i class="fa fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div class="send-email-modal__section-actions">
        <button
          type="button"
          class="send-email-modal__close-section-btn"
          @click="emitCloseDocumentAttachments"
        >
          {{ resolvedText.closeSectionLabel }}
        </button>
      </div>
    </div>

    <div v-if="showDocumentAttachments" class="send-email-modal__documents-footer-gap" aria-hidden="true"></div>
  </div>
</template>

<script>
export default {
  name: 'SendEmailInvoiceAttachmentsSection',
  props: {
    text: {
      type: Object,
      default: () => ({}),
    },
    showInvoiceAttachmentsAction: {
      type: Boolean,
      default: true,
    },
    showInvoiceAttachments: {
      type: Boolean,
      default: false,
    },
    showDocumentAttachments: {
      type: Boolean,
      default: false,
    },
    isExistingDocumentMode: {
      type: Boolean,
      default: true,
    },
    selectedInvoiceAttachments: {
      type: Array,
      default: () => [],
    },
    normalizedInvoiceAttachmentOptions: {
      type: Array,
      default: () => [],
    },
    selectedDocumentAttachments: {
      type: Array,
      default: () => [],
    },
    normalizedDocumentAttachmentOptions: {
      type: Array,
      default: () => [],
    },
    newDocumentUploads: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'toggle-invoice-attachments',
    'toggle-document-attachments',
    'close-invoice-attachments',
    'close-document-attachments',
    'set-document-attachment-mode',
    'update:selected-invoice-attachments',
    'update:selected-document-attachments',
    'new-document-change',
    'add-new-document-upload-row',
    'remove-new-document-upload-row',
  ],
  computed: {
    resolvedText() {
      return this.text && typeof this.text === 'object' ? this.text : {};
    },
    selectedInvoiceAttachmentsProxy: {
      get() {
        return Array.isArray(this.selectedInvoiceAttachments) ? this.selectedInvoiceAttachments : [];
      },
      set(nextValue) {
        this.$emit('update:selected-invoice-attachments', Array.isArray(nextValue) ? nextValue : []);
      },
    },
    selectedDocumentAttachmentsProxy: {
      get() {
        return Array.isArray(this.selectedDocumentAttachments) ? this.selectedDocumentAttachments : [];
      },
      set(nextValue) {
        this.$emit('update:selected-document-attachments', Array.isArray(nextValue) ? nextValue : []);
      },
    },
  },
  methods: {
    emitToggleInvoiceAttachments() {
      this.$emit('toggle-invoice-attachments');
    },
    emitToggleDocumentAttachments() {
      this.$emit('toggle-document-attachments');
    },
    emitCloseInvoiceAttachments() {
      this.$emit('close-invoice-attachments');
    },
    emitCloseDocumentAttachments() {
      this.$emit('close-document-attachments');
    },
    emitSetDocumentAttachmentMode(mode) {
      this.$emit('set-document-attachment-mode', mode);
    },
    emitNewDocumentChange(uploadId, event) {
      this.$emit('new-document-change', uploadId, event);
    },
    emitAddNewDocumentUploadRow() {
      this.$emit('add-new-document-upload-row');
    },
    emitRemoveNewDocumentUploadRow(uploadId) {
      this.$emit('remove-new-document-upload-row', uploadId);
    },
    openNativeDocumentPicker(uploadId) {
      const inputRef = this.$refs[`newDocInput-${uploadId}`];
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;

      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
  },
};
</script>
